<template>
	<v-select
		v-if="inputType === 'select'"
		v-model="fieldValue"
		:items="selectOptions"
		:disabled="disabled"
		:readonly="readonly"
	>
		<template #label>
			<span v-if="meta.required">
				<strong>*</strong>
			</span>
			{{ displayName }}
		</template>
	</v-select>
	<v-autocomplete
		v-else-if="inputType === 'path'"
		v-model="fieldValue"
		:items="pathOptions"
		:disabled="disabled"
		:readonly="readonly"
	>
		<template #label>
			<span v-if="meta.required">
				<strong>*</strong>
			</span>
			{{ displayName }}
		</template>
	</v-autocomplete>
	<v-switch
		v-else-if="inline && inputType === 'checkbox'"
		v-model="fieldValue"
		:disabled="disabled"
		:readonly="readonly"
	/>
	<v-checkbox
		v-else-if="!inline && inputType === 'checkbox'"
		v-model="fieldValue"
		:disabled="disabled"
		:readonly="readonly"
	>
		<template #label>
			<span v-if="meta.required">
				<strong class="mr-1">*</strong>
			</span>
			{{ displayName }}
		</template>
	</v-checkbox>
	<v-text-field
		v-else
		v-model="fieldValue"
		:type="textInputType"
		:rules="inputRules"
		:disabled="disabled"
		:readonly="readonly"
	>
		<template #label>
			<span v-if="meta.required">
				<strong>*</strong>
			</span>
			{{ displayName }}
		</template>
	</v-text-field>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";

import { humanizeNodeName, closestParentDevice } from "@/utils/k4util";

import base from "./base";

export default {
	extends: base,

	props: {
		value: { type: null, required: true },
		disabled: { type: Boolean, default: false },
		readonly: { type: Boolean, default: false },
		required: { type: Boolean, default: false },
		inline: { type: Boolean, default: false },
	},

	computed: {
		...mapGetters({ liveModel: "controller/nodes/$model" }),

		fieldValue: {
			get() {
				return this.value;
			},
			set(val) {
				if (this.textInputType === "number" && _.size(val)) {
					val = Number(val);
				}
				this.$emit("input", val);
			},
		},

		inputRules() {
			const rules = [];
			const { required, type, options } = this.meta;

			if (required || this.required) {
				rules.push((v) => !!v || _.isNumber(v) || "Required");
			}

			if (type === "number" && options) {
				const { min, max } = options;
				if (!_.isNil(min))
					rules.push(
						(v) => parseFloat(v) >= min || `Value must be greater than ${min}`
					);
				if (!_.isNil(max))
					rules.push(
						(v) => parseFloat(v) <= max || `Value must be less than ${max}`
					);
			}
			return rules;
		},

		selectOptions() {
			return _.map(this.meta.options, (option) => ({
				text: _.startCase(option),
				value: option,
			}));
		},

		pathOptions() {
			return _.chain(this.liveModel.paths)
				.pick(this.meta.options)
				.values()
				.flatten()
				.map((node) => {
					let parentNode = node;
					const names = [humanizeNodeName(node)];
					while ((parentNode = closestParentDevice(parentNode))) {
						const parentName = humanizeNodeName(parentNode);
						names.push(parentName);
					}
					return {
						text: names.reverse().join(" > "),
						value: node.path(),
					};
				})
				.value();
		},
	},

	mounted() {
		// making sure a required checkbox still sends a value if not clicked
		if (this.meta.required && this.inputType === "checkbox") {
			this.$emit("input", this.value || false);
		}
	},
};
</script>
